/**
 * Send a single itinerary item to the traveler's own calendar app.
 *
 * Two delivery paths, because the platforms don't share one:
 *  - Apple (and any desktop calendar client): an .ics download, which macOS/iOS
 *    hand straight to Calendar.app.
 *  - Google: the `render?action=TEMPLATE` web form, pre-filled and opened in a
 *    new tab so the traveler just confirms.
 */

import {
  CalendarEventDraft,
  buildIcsForEvents,
  downloadIcs,
  toIcsFileName,
} from '../../services/trips/utils/ical';

export type CalendarProvider = 'apple' | 'google';

export type { CalendarEventDraft };

export const CALENDAR_PROVIDER_LABELS: Record<CalendarProvider, string> = {
  apple: 'Apple Calendar',
  google: 'Google Calendar',
};

const GOOGLE_CALENDAR_TEMPLATE_URL = 'https://calendar.google.com/calendar/render';
const DEFAULT_DURATION_MS = 60 * 60 * 1000;

/**
 * Which calendar the device most likely owns. iPhone/iPad/Mac get Apple,
 * Android gets Google; anything else falls back to Google (no desktop client
 * assumption on Windows/Linux, where an .ics may have no handler at all).
 */
export function detectDefaultCalendarProvider(): CalendarProvider {
  if (typeof navigator === 'undefined') return 'google';

  const userAgent = navigator.userAgent || '';

  if (/Android/i.test(userAgent)) return 'google';
  if (/iPad|iPhone|iPod/i.test(userAgent)) return 'apple';
  // Also catches iPadOS 13+, which reports itself as a Mac — either way the
  // .ics is the right hand-off.
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'apple';

  return 'google';
}

function resolveEnd(event: CalendarEventDraft): Date {
  const { start, end } = event;
  if (end && !Number.isNaN(end.getTime()) && end > start) return end;
  return new Date(start.getTime() + DEFAULT_DURATION_MS);
}

// Google's `dates` param wants the compact UTC form: YYYYMMDDTHHMMSSZ.
function toCompactUtc(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

export function buildGoogleCalendarUrl(event: CalendarEventDraft): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || 'Activity',
    dates: `${toCompactUtc(event.start)}/${toCompactUtc(resolveEnd(event))}`,
  });

  if (event.location) params.set('location', event.location);
  if (event.description) params.set('details', event.description);

  return `${GOOGLE_CALENDAR_TEMPLATE_URL}?${params.toString()}`;
}

/** Add one event to the given calendar. */
export function addEventToCalendar(
  event: CalendarEventDraft,
  provider: CalendarProvider,
): void {
  if (provider === 'google') {
    window.open(buildGoogleCalendarUrl(event), '_blank', 'noopener,noreferrer');
    return;
  }

  const name = event.title || 'Activity';
  downloadIcs(buildIcsForEvents([event], name), toIcsFileName(name));
}
